import {
  AddOnFeeCategory,
  AddOnFees,
  ContractClassification,
  ESenderRole,
  ETrasactionsType,
  FeeCalculationType,
  IContractDetailsResponseData,
  UserParty,
} from "../api/services/contract/models";

/**
 * Matches an entry of `partiesProfiles` back to its entry in `parties`.
 *
 * Only parties of the profile's own role are considered: the same person can
 * hold more than one role in a milestone (or several parties can still be
 * unregistered), and a match across roles shows another party's amount and
 * status. Within the role a party is matched by its user id, then by phone
 * number, then by id — and each key only when both sides carry it.
 */
export const findPartyForProfile = (
  parties: UserParty[] | undefined,
  profile:
    | {
        id?: number | string;
        phoneNumber?: string;
        userIdentification?: number | string;
      }
    | undefined,
  role: ESenderRole
) => {
  const roleParties = parties?.filter((party) => party?.role === role);
  const has = (value?: number | string | null) =>
    value !== undefined && value !== null && String(value) !== "";

  return (
    roleParties?.find(
      (party) =>
        has(profile?.userIdentification) &&
        has(party?.userId) &&
        String(party.userId) === String(profile?.userIdentification)
    ) ??
    roleParties?.find(
      (party) =>
        has(profile?.phoneNumber) && party?.phoneNumber === profile?.phoneNumber
    ) ??
    roleParties?.find((party) => {
      const partyId = (party as { id?: number | string })?.id;
      return (
        has(profile?.id) &&
        has(partyId) &&
        String(partyId) === String(profile?.id)
      );
    })
  );
};

const HARAJ_CLASSIFICATIONS = [
  ContractClassification.BASIC_HARAJ,
  ContractClassification.STOCK_CONTRACT_HARAJ,
];

/**
 * What a customer of the contract has to pay, or paid. A party's amount comes
 * from `parties`, except for a Haraj contract after the customer paid: that
 * amount is the successful CUSTOMER_PAY / MANUAL_BANK_TRANSFER transaction.
 */
export const getCustomerAmount = (
  contract: IContractDetailsResponseData | undefined,
  party: UserParty | undefined,
  profile?: { id?: number | string; userIdentification?: number | string }
): number => {
  const partyAmount = party?.amount || 0;
  if (
    !HARAJ_CLASSIFICATIONS.includes(
      contract?.contractClassification as ContractClassification
    )
  )
    return partyAmount;

  const paidTransactions = contract?.transactions?.filter(
    (transaction) =>
      (transaction?.type === ETrasactionsType.CUSTOMER_PAY ||
        transaction?.type === ETrasactionsType.MANUAL_BANK_TRANSFER) &&
      transaction?.status === "SUCCESS"
  );
  const customerIds = [party?.userId, profile?.userIdentification, profile?.id]
    .filter((value) => value !== undefined && value !== null)
    .map(String);
  const isSingleCustomer =
    (contract?.parties?.filter((p) => p?.role === ESenderRole.CUSTOMER)
      .length || 0) <= 1;

  const paidTransaction =
    paidTransactions?.find(
      (transaction) =>
        !!transaction?.fromUserId &&
        customerIds.includes(String(transaction.fromUserId))
    ) ?? (isSingleCustomer ? paidTransactions?.[0] : undefined);

  return paidTransaction?.amount ?? partyAmount;
};

export const getShipmentFeesTotal = (
  contract?: IContractDetailsResponseData
) => {
  if (!contract?.addonFeesList?.length || contract?.itemPrice == null) {
    return contract?.shipment?.fee ?? 0;
  }

  const addonShippingFees = contract?.addonFeesList?.filter(
    (fee) => fee.type === AddOnFeeCategory.SHIPPING_FEES
  );
  const total =
    addonShippingFees?.reduce?.((acc: number, addOnFee: AddOnFees) => {
      if (addOnFee.feeType === FeeCalculationType.FLAT) {
        return acc + addOnFee.amount;
      } else if (addOnFee.feeType === FeeCalculationType.PERCENTAGE) {
        return acc + (contract?.itemPrice * addOnFee.amount) / 100;
      }
      return acc;
    }, 0) ??
    contract?.shipment?.fee ??
    0;
  return total;
};
